<template>
	<MediaTagSelectionDialog v-model:search="search" :open="dialogVisible" :items="sortedItems" :loading="loading" :saving="saving" :z-index="zIndex"
		:search-label="items.length > 0 || loading ? 'Search Tags' : 'Create your first tag!'"
		:search-action-enabled="!loading"
		:search-icon="loading || search.length === 0 ? 'icon-search-magnifying-glass' : 'icon-plus'"
		:search-props="{ ...searchFieldProps(search), messages: countCharacters(search) > TAG_MAX_LENGTH ? [`New tag names must be ${TAG_MAX_LENGTH} characters or fewer.`] : [] }"
		:loading-text="loadingText" :empty-text="items.length > 0 ? noMatchText : noDataText"
		@cancel="handleCancel" @agree="handleAgree" @toggle="toggleItem" @search-action="handleEvent">
		<template #chip="{ item }"><Chip :label="item" /></template>
		<template v-if="canCreateFromSearch" #create>
			<div v-test="'tag-selection-create-action'" class="create-tag-fixed text-center pt-0 pb-2 w-100" @mousedown.prevent @click.stop="handleEvent">
				<hr v-if="sortedItems.length" class="mb-2" />
				<v-btn block color="transparent" class="text-none create-action-btn">Create tag '<span class="create-action-query">{{ search.trim() }}</span>'</v-btn>
			</div>
		</template>
	</MediaTagSelectionDialog>
	<EditTagDialog ref="editTagDialog" :z-index="zIndex + 1" />
</template>
<script lang="ts">
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import api from "@/api";
	import Logging from "../../../plugin/track";
	import ResponseArray from "@/types/ResponseArray";
	import { type Label } from "@/types/Label";
	import Chip from "@/components/tags/DisplayChip.vue";
	import EditTagDialog from "@/components/tags/EditTagDialog.vue";
	import { TAG_MAX_LENGTH } from "@/api/constants";
	import MediaTagSelectionDialog from "@common-ui/media/MediaTagSelectionDialog.vue";
	import { countCharacters } from "@/helpers/utilities";

	interface TagSelectionDialogData {
		loading: boolean;
		items: Label[];
		selectedItems: Label[];
		itemsOnServer: number;
		noDataText: string;
		noMatchText: string;
		loadingText: string;
		currentPage: number;
		itemsPerPage: number;
		search: string;
		virtualScrollHeight: number;
		isDirty: boolean;
		sourceSelectedItems: Label[] | null;
	}
	export default {
		components: {
			Chip,
			MediaTagSelectionDialog,
			EditTagDialog
		},
		// LSP-20845: the template became a fragment root (Dialog + EditTagDialog), so the caller's
		// @cancel/@agree can no longer auto-inherit — they must be declared component events.
		emits: ["cancel", "agree", "error"],
		setup() {
			return { TAG_MAX_LENGTH, countCharacters };
		},
		props: {
			dialogVisible: {
				type: Boolean,
				default: false
			},
			zIndex: {
				type: Number,
				default: 9000
			},
			preSelectedItems: {
				type: Array as () => Label[],
				default: () => []
			},
			maxTags: {
				type: Number,
				default: Number.MAX_SAFE_INTEGER
			},
			/**
			 * If true, the dialog is being used for bulk editing and should not preselect any existing tags
			 */
			isBulk: {
				type: Boolean,
				default: false
			},
			saving: {
				type: Boolean,
				default: false
			}
		},
		data(): TagSelectionDialogData {
			return {
				loading: false,
				items: [] as Label[],
				selectedItems: [] as Label[],
				itemsOnServer: 0,
				search: "",
				noDataText: "It looks like you have no tags yet. Go ahead and create your first one!",
				// LSP-20845 (round 2): only reachable when the search can't create (e.g. over the length
				// limit) — the old "Click '+'" hint is replaced by the inline "Create tag 'x'" button, and
				// this copy no longer claims "you have no tags yet" when tags simply don't match.
				noMatchText: "No tags match your search.",
				loadingText: "Fetching your tags...",
				currentPage: 1,
				itemsPerPage: 9999, // Removed paging to be consistent with the other selecting components.
				virtualScrollHeight: 290,
				isDirty: false,
				sourceSelectedItems: [] as Label[],
				sourceSelectedNames: new Set() as Set<string>
			};
		},
		computed: {
			sortedItems() {
				return [...this.filteredItems].sort(this.sortItems);
			},
			filteredItems() {
				if (exceedsSearchLimit(this.search)) return [];
				if (!this.search) return this.items;
				return this.items.filter((item) => item.name.toLowerCase().includes(this.search.toLowerCase()));
			},
			// LSP-20845 (round 2): same rule as ComboBox.shouldShowCreateButton — a non-empty,
			// within-limit search with no EXACT name match offers the inline "Create tag 'x'" action.
			canCreateFromSearch(): boolean {
				const term = this.search.trim();
				if (this.loading || !term || countCharacters(term) > TAG_MAX_LENGTH) {
					return false;
				}
				const lower = term.toLowerCase();
				return !this.items.some((item) => (item?.name || "").trim().toLowerCase() === lower);
			}
		},

		watch: {
			dialogVisible(val) {
				if (val) {
					this.isDirty = false;
					this.search = "";
					this.updateSelectedItems(this.preSelectedItems);
					this.sourceSelectedNames = new Set(this.preSelectedItems.map((x) => x.name));
					this.loadTags();
				}
			},
			preSelectedItems: {
				handler(newVal: Label[]): void {
					this.updateSelectedItems(newVal);
					this.sourceSelectedNames = new Set(newVal.map((x) => x.name));
				}
			},
			selectedItems: {
				deep: true,
				handler(newVal: Label[]): void {
					// Compare sets of names to determine dirty state
					const currentNames = new Set(newVal.map((x) => x.name));

					let isDirtyCalc = this.sourceSelectedNames.size !== currentNames.size;
					if (!isDirtyCalc) {
						for (const name of currentNames) {
							if (!this.sourceSelectedNames.has(name)) {
								isDirtyCalc = true;
								break;
							}
						}
					}

					this.isDirty = isDirtyCalc;
				}
			}
		},

		mounted(): void {
			this.updateSelectedItems(this.preSelectedItems);
		},
		methods: {
			searchFieldProps,
			async loadTags(): Promise<void> {
				// Prevent multiple concurrent API calls
				if (this.loading) {
					return;
				}

				this.loading = true;

				try {
					const resp = (await (api as any).getLabels({
						page: this.currentPage,
						pageSize: this.itemsPerPage
					})) as ResponseArray<Label>;
					this.itemsOnServer = resp.count;
					// Set the selected state of the items based on the selected items
					const selectedNames = new Set(this.selectedItems.map((x) => x.name));
					if (Array.isArray(resp?.rows)) {
						resp.rows.forEach((item) => (item.selected = selectedNames.has(item.name)));
						this.items = resp.rows;
					} else {
						this.items = [];
					}
				} catch (err: any) {
					console.error(err, {
						extra: {
							currentPage: this.currentPage,
							itemsPerPage: this.itemsPerPage
						}
					});
					this.noDataText = "There was an issue loading your organization's tags. Please refresh the page to try again.";

					// Show user-facing error message
					this.$emit("error", "Failed to load tags. Please try again.");
				} finally {
					this.loading = false;
				}
			},

			toggleItem(item: Label, bypassUntoggle: boolean = false): void {
				if (!item.selected) {
					this.selectedItems.push(item);
				} else {
					if (bypassUntoggle) {
						return;
					}
					const index = this.selectedItems.findIndex((x) => x.name.localeCompare(item.name) === 0);
					if (index !== -1) {
						this.selectedItems.splice(index, 1);
					}
				}
				item.selected = !item.selected;
			},
			handleEvent(): void {
				this.addTag(this.search, true);
			},
			async addTag(tag: string, bypassUntoggle: boolean = false): Promise<void> {
				tag = tag.trim();
				if (this.loading || !tag || countCharacters(tag) > TAG_MAX_LENGTH) {
					return;
				}
				const existingItem = this.sortedItems.find((item) => item.name.toLowerCase() === tag.toLowerCase());
				if (existingItem) {
					this.toggleItem(existingItem, bypassUntoggle);
					this.search = "";
					return;
				}
				// LSP-20845: route creation through the shared "Create a New Tag" dialog (name + color,
				// Create/Cancel) so this panel matches every other tag surface, instead of silently
				// pushing a name-only local row. The dialog creates the org tag server-side and returns
				// the full record; Apply then attaches it to the media.
				const dialog = this.$refs.editTagDialog as { open: (defaultName?: string) => Promise<Label | null> } | null;
				if (!dialog) return;
				const record = await dialog.open(tag);
				if (record) {
					const alreadyListed = this.items.find((item) => item.id === record.id);
					if (alreadyListed) {
						this.toggleItem(alreadyListed, true);
					} else {
						this.items.push(record);
						this.toggleItem(record);
					}
					this.search = "";
				}
			},
			trackDialogClick(action: "cancel" | "agree") {
				const testId = `dialog-${action}-button`;
				Logging.log({ name: `${testId}-clicked`, data: { action: "click", testId, node: "button" } });
			},
			handleCancel(event?: MouseEvent) {
				if (event) this.trackDialogClick("cancel");
				this.$emit("cancel");
			},
			handleAgree(event?: MouseEvent) {
				if (event) this.trackDialogClick("agree");
				// LSP-20001: Apply is never greyed out (CLAUDE.md "Form action buttons — always enabled,
				// no-op when invalid"); the click is what no-ops. With the selection unchanged there is
				// nothing to save, so close the dialog instead of emitting `agree` — every caller's
				// @agree handler issues a tag write, and firing one here would be a redundant request
				// (and, for a freshly typed tag, a duplicate submission). Cancel keeps its own discard
				// semantics; both leave the media exactly as it was.
				if (!this.isDirty) {
					this.$emit("cancel");
					return;
				}
				this.$emit("agree", this.selectedItems);
			},

			updateSelectedItems(items: Label[]): void {
				this.selectedItems.length = 0;
				if (this.isBulk) {
					// No preselection for bulk edit mode
					this.items.forEach((item) => (item.selected = false));
					return;
				}

				const selectedNames = new Set(items.map((x) => x.name));
				this.items.forEach((item) => (item.selected = selectedNames.has(item.name)));
				this.selectedItems.push(...items);
			},
			sortItems(a: Label, b: Label) {
				const aSelected = a.selected;
				const bSelected = b.selected;

				// First sort by selection status
				if (aSelected && !bSelected) return -1;
				if (!aSelected && bSelected) return 1;

				return a.name.localeCompare(b.name);
			}
		}
	};
</script>
<style scoped>
	/* LSP-20845 (round 2): same sticky bottom row as ComboBox's create-tag-fixed, pinned to the
	   bottom of this dialog's own scroll container so the create action stays visible while the
	   partial-match list scrolls. */
	.create-tag-fixed {
		position: sticky;
		bottom: 0;
		background-color: white;
	}
</style>
